import React, { useEffect, useRef, useState } from "react";
import { Check } from "lucide-react";
import { Spinner } from "./ui/kit";
import api, { apiError } from "../lib/api";

/**
 * Firma z ARES (deska 18): bez dalšího pole, jen v IČO.
 *   • IcoAres — po 8. číslici se firma sama načte (název, DIČ, adresa).
 *   • AresProuzek — „Doplněno z ARES“ + plátce DPH + Zrušit (vrátí formulář, jak byl).
 * Formulář si drží stav přes useAres(): co bylo předtím (kvůli Zrušit) a krátké
 * zezelenání doplněných polí (třída `ares-svit` na .field).
 */
export function useAres() {
  const [firma, setFirma] = useState(null);
  const [svit, setSvit] = useState(false);
  const pred = useRef(null);
  const t = useRef(null);
  useEffect(() => () => clearTimeout(t.current), []);
  return {
    firma,
    svit: svit ? "ares-svit" : "",
    /** Zapamatuje si formulář před doplněním (s napsaným IČO) a rozsvítí pole. */
    prevzit(nalezena, puvodni) {
      if (!firma) pred.current = puvodni;
      setFirma(nalezena); setSvit(true);
      clearTimeout(t.current); t.current = setTimeout(() => setSvit(false), 900);
    },
    /** Vrátí formulář před doplněním. */
    zrusit() { const p = pred.current; setFirma(null); pred.current = null; return p; },
  };
}

const ciste = (s) => (s || "").replace(/\s/g, "");

export function IcoAres({ value, onChange, onNalezeno, disabled = false, testid, className = "" }) {
  const [nacita, setNacita] = useState(false);
  const [hlaska, setHlaska] = useState("");
  const posledni = useRef(ciste(value));
  const poradi = useRef(0);

  const nacti = async (ico) => {
    if (disabled || ico === posledni.current) return;
    posledni.current = ico;
    const moje = ++poradi.current;
    setNacita(true);
    try {
      const { data } = await api.get(`/api/ares/ico/${ico}`);
      if (moje === poradi.current) onNalezeno(data);
    } catch (e) {
      if (moje === poradi.current) setHlaska(e.response?.status === 404 ? "V ARES nenalezeno." : apiError(e.response?.data?.detail) || "ARES teď neodpovídá.");
    } finally { if (moje === poradi.current) setNacita(false); }
  };

  const zmena = (v) => {
    onChange(v);
    setHlaska("");
    const ico = ciste(v);
    if (/^\d{8}$/.test(ico)) nacti(ico);
  };

  // Starší IČO lidé píšou bez úvodních nul (9691120 = 09691120). Během psaní
  // to nejde poznat, proto se krátké IČO doplní nulami až při opuštění pole.
  const opusteni = () => {
    const ico = ciste(value);
    if (disabled || !/^\d{6,7}$/.test(ico)) return;
    const plne = ico.padStart(8, "0");
    onChange(plne);
    nacti(plne);
  };

  return (
    <div className="relative">
      <input className={`field amount pr-[64px] ${className}`} value={value} disabled={disabled} inputMode="numeric" autoComplete="off"
        placeholder={disabled ? "" : "8 číslic"} data-testid={testid} onChange={(e) => zmena(e.target.value)} onBlur={opusteni}
        onKeyDown={(e) => { if (e.key === "Enter" && /^\d{6,7}$/.test(ciste(value))) { e.preventDefault(); opusteni(); } }} />
      {!disabled && (
        <span title="Po zadání IČO se název, DIČ a adresa doplní z veřejného registru ARES."
          className="absolute right-2 top-[22px] md:top-5 -translate-y-1/2 h-[22px] px-2 rounded-full bg-white text-muted text-[10px] font-800 tracking-[.06em] inline-flex items-center gap-1 shadow-[inset_0_0_0_1px_#e6e5e1]">
          {nacita && <Spinner className="!h-3 !w-3" />}ARES
        </span>
      )}
      {hlaska && <span className="block mt-1 text-[11px] text-muted-2" data-testid={`${testid}-hlaska`}>{hlaska}</span>}
    </div>
  );
}

export function AresProuzek({ firma, onZrusit, testid = "ares-prouzek" }) {
  if (!firma) return null;
  return (
    <div className="rise flex items-center gap-2.5 px-3 py-2.5 rounded-[14px] bg-success-bg text-success t-meta md:text-[13px] font-600" data-testid={testid}>
      <span className="h-[22px] w-[22px] rounded-full bg-success text-white inline-flex items-center justify-center shrink-0"><Check size={13} strokeWidth={3} /></span>
      <span className="flex-1 min-w-0 truncate">Doplněno z ARES</span>
      <span className={`h-[22px] px-2 rounded-full bg-white text-[10px] font-800 tracking-[.06em] uppercase inline-flex items-center shrink-0 ${firma.platce_dph ? "text-success" : "text-muted"}`}>
        {firma.platce_dph ? "Plátce DPH" : "Neplátce DPH"}
      </span>
      <button type="button" onClick={onZrusit} className="tap md:min-h-0 px-1 text-sec font-700 hover:text-ink" data-testid={`${testid}-zrusit`}>Zrušit</button>
    </div>
  );
}
